import { cashCollectionAppServiceApi } from "../../../app/cashCollection/cashCollectionAppServiceApi";
import { BASE_API_CASH_COLLECTION } from "../../../constants/apiUrl";
import { GetQueryArgsType } from "../../../types";
import {
  CheckAvailabilityResponse,
  GetAllAreaDataParamsType,
  GetAllAreaDataType,
  GetAllRouteDataParamsType,
  GetAllRouteDataType,
  GetAllTerritoryDataParamsType,
  GetAllTerritoryDataType,
  GetAllZoneDataParamsType,
  GetAllZoneDataType,
  GetSingleAreaDataType,
  GetSingleRouteDataType,
  GetSingleTerritoryDataType,
  GetSingleZoneDataType,
} from "../../../types/cashCollection/geoSetup";

export const geoSetupApi = cashCollectionAppServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    // GEO Setup Zone
    createZone: builder.mutation({
      query: (data) => ({
        url: BASE_API_CASH_COLLECTION.createZone,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetZone"];
        return [];
      },
    }),

    updateZone: builder.mutation({
      query: ({ id, data }) => ({
        url: `${BASE_API_CASH_COLLECTION.updateZone}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetZone"];
        return [];
      },
    }),

    getAllZone: builder.query<
      GetAllZoneDataType,
      GetQueryArgsType<GetAllZoneDataParamsType>
    >({
      query: (queryArgs) => ({
        url: BASE_API_CASH_COLLECTION.getAllZone,
        params: queryArgs.params,
      }),
      providesTags: ["GetZone"],
    }),

    getSingleZone: builder.query<GetSingleZoneDataType, any>({
      query: (id) => ({
        url: `${BASE_API_CASH_COLLECTION.getSingleZone}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetZone", id }],
    }),

    // GEO Setup Area
    createArea: builder.mutation({
      query: (data) => ({
        url: BASE_API_CASH_COLLECTION.createArea,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetArea"];
        return [];
      },
    }),

    updateArea: builder.mutation({
      query: ({ id, data }) => ({
        url: `${BASE_API_CASH_COLLECTION.updateArea}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetArea"];
        return [];
      },
    }),

    getAllArea: builder.query<
      GetAllAreaDataType,
      GetQueryArgsType<GetAllAreaDataParamsType>
    >({
      query: (queryArgs) => ({
        url: BASE_API_CASH_COLLECTION.getAllArea,
        params: queryArgs.params,
      }),
      providesTags: ["GetArea"],
    }),

    getSingleArea: builder.query<GetSingleAreaDataType, any>({
      query: (id) => ({
        url: `${BASE_API_CASH_COLLECTION.getSingleArea}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetArea", id }],
    }),

    // GEO Setup Territory
    createTerritory: builder.mutation({
      query: (data) => ({
        url: BASE_API_CASH_COLLECTION.createTerritory,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetTerritory"];
        return [];
      },
    }),

    updateTerritory: builder.mutation({
      query: ({ id, data }) => ({
        url: `${BASE_API_CASH_COLLECTION.updateTerritory}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetTerritory"];
        return [];
      },
    }),

    getAllTerritory: builder.query<
      GetAllTerritoryDataType,
      GetQueryArgsType<GetAllTerritoryDataParamsType>
    >({
      query: (queryArgs) => ({
        url: BASE_API_CASH_COLLECTION.getAllTerritory,
        params: queryArgs.params,
      }),
      providesTags: ["GetTerritory"],
    }),

    getSingleTerritory: builder.query<GetSingleTerritoryDataType, any>({
      query: (id) => ({
        url: `${BASE_API_CASH_COLLECTION.getSingleTerritory}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetTerritory", id }],
    }),

    // GEO Setup Route
    createRoute: builder.mutation({
      query: (data) => ({
        url: BASE_API_CASH_COLLECTION.createRoute,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetRoute"];
        return [];
      },
    }),

    updateRoute: builder.mutation({
      query: ({ id, data }) => ({
        url: `${BASE_API_CASH_COLLECTION.updateRoute}/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetRoute", "GetRouteSingle"];
        return [];
      },
    }),

    getAllRoute: builder.query<
      GetAllRouteDataType,
      GetQueryArgsType<GetAllRouteDataParamsType>
    >({
      query: (queryArgs) => ({
        url: BASE_API_CASH_COLLECTION.getAllRoute,
        params: queryArgs.params,
      }),
      providesTags: ["GetRoute"],
    }),

    getSingleRoute: builder.query<GetSingleRouteDataType, any>({
      query: (id) => ({
        url: `${BASE_API_CASH_COLLECTION.getSingleRoute}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: "GetRouteSingle", id }],
    }),

    createVirtualAccount: builder.mutation({
      query: ({ id, data }) => ({
        url: `${BASE_API_CASH_COLLECTION.virtualAccount}/${id}/bank-account`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result) return ["GetRoute", "GetRouteSingle"];
        return [];
      },
    }),

    checkAccountNumberAvailability: builder.query<
      CheckAvailabilityResponse,
      GetQueryArgsType<{ msisdn: string | number }>
    >({
      query: (queryArgs) => ({
        url: BASE_API_CASH_COLLECTION.checkAccountNumberAvailability,
        params: queryArgs.params,
      }),
      providesTags: ["GetRoute"],
    }),
  }),
});

export const {
  useCreateZoneMutation,
  useUpdateZoneMutation,
  useGetAllZoneQuery,
  useGetSingleZoneQuery,
  useCreateAreaMutation,
  useUpdateAreaMutation,
  useGetAllAreaQuery,
  useGetSingleAreaQuery,
  useCreateTerritoryMutation,
  useUpdateTerritoryMutation,
  useGetAllTerritoryQuery,
  useGetSingleTerritoryQuery,
  useCreateRouteMutation,
  useUpdateRouteMutation,
  useGetAllRouteQuery,
  useGetSingleRouteQuery,
  useCreateVirtualAccountMutation,
  useCheckAccountNumberAvailabilityQuery,
} = geoSetupApi;
